import { type BaseChannel } from "@sendbird/chat";
import { type GroupChannel, GroupChannelHandler } from "@sendbird/chat/groupChannel";
import { type BaseMessage, type PreviousMessageListQuery, ReplyType } from "@sendbird/chat/message";
// FIXME: Automatically disabled during ESLint config update
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { logError } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { MESSAGE_FETCH_LIMIT } from "../constants";
import { type ChatMessage } from "../types";
import { shouldHideMessage, transformSendbirdMessage } from "../utils/transformMessages";

interface UseChatMessagesParams {
  channel: GroupChannel | null;
  enabled?: boolean;
}

interface UseChatMessagesResult {
  messages: ChatMessage[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  isLoadingMore: boolean;
  refetch: () => void;
}

/**
 * Hook to fetch and manage chat messages from a Sendbird channel.
 * Supports pagination and real-time message updates.
 *
 * @example
 * ```tsx
 * const { data: channel } = useChatChannel({ workplaceId, workerId });
 * const { messages, isLoading, hasMore, loadMore } = useChatMessages({ channel });
 * ```
 */
export function useChatMessages({
  channel,
  enabled = true,
}: UseChatMessagesParams): UseChatMessagesResult {
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const messageQueryRef = useRef<PreviousMessageListQuery | null>(null);

  // Initial message fetch
  const query = useQuery({
    queryKey: ["chat", "messages", channel?.url],
    queryFn: async (): Promise<ChatMessage[]> => {
      if (!channel) {
        return [];
      }

      const messageQuery = channel.createPreviousMessageListQuery({
        limit: MESSAGE_FETCH_LIMIT,
        reverse: false,
        replyType: ReplyType.ALL, // Include both regular messages and replies
        includeParentMessageInfo: true, // Include parent message info for replies
      });

      messageQueryRef.current = messageQuery;

      const sendbirdMessages: BaseMessage[] = await messageQuery.load();
      setHasMore(messageQuery.hasNext);

      return sendbirdMessages
        .map(transformSendbirdMessage)
        .filter((message): message is ChatMessage => message !== null)
        .filter((message) => !shouldHideMessage(message.customType));
    },
    enabled: enabled && Boolean(channel),
    staleTime: 0, // Always fetch fresh messages
  });

  // Update local state when query data changes
  useEffect(() => {
    if (query.data) {
      setMessages(query.data);
    }
  }, [query.data]);

  // Load more messages (pagination)
  const loadMore = async () => {
    if (!messageQueryRef.current || !hasMore || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const olderMessages: BaseMessage[] = await messageQueryRef.current.load();
      const transformedMessages = olderMessages
        .map(transformSendbirdMessage)
        .filter((message): message is ChatMessage => message !== null)
        .filter((message) => !shouldHideMessage(message.customType));

      setMessages((previous) => [...transformedMessages, ...previous]);
      setHasMore(messageQueryRef.current.hasNext);
    } catch (error) {
      logError("Failed to load more chat messages", { error });
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Real-time message handler
  useEffect(() => {
    if (!channel || !sdk) {
      return;
    }

    const handlerId = `chat-messages-${channel.url}-${Date.now()}`;

    const channelHandler = new GroupChannelHandler({
      onMessageReceived: (receivedChannel: BaseChannel, message: BaseMessage) => {
        if (receivedChannel.url === channel.url) {
          const transformedMessage = transformSendbirdMessage(message);
          if (transformedMessage && !shouldHideMessage(transformedMessage.customType)) {
            setMessages((previous) => [...previous, transformedMessage]);
          }
        }
      },
      onMessageUpdated: (receivedChannel: BaseChannel, message: BaseMessage) => {
        if (receivedChannel.url === channel.url) {
          const transformedMessage = transformSendbirdMessage(message);
          if (transformedMessage && !shouldHideMessage(transformedMessage.customType)) {
            setMessages((previous) =>
              previous.map((previousMessage) =>
                previousMessage.messageId === transformedMessage.messageId
                  ? transformedMessage
                  : previousMessage
              )
            );
          }
        }
      },
      onMessageDeleted: (receivedChannel: BaseChannel, messageId: number) => {
        if (receivedChannel.url === channel.url) {
          setMessages((previous) => previous.filter((message) => message.messageId !== messageId));
        }
      },
    });

    sdk.groupChannel.addGroupChannelHandler(handlerId, channelHandler);

    return () => {
      sdk.groupChannel.removeGroupChannelHandler(handlerId);
    };
  }, [channel, sdk]);

  // Mark messages as read when channel is active
  useEffect(() => {
    if (channel && messages.length > 0) {
      channel.markAsRead().catch((error) => {
        logError("Failed to mark chat messages as read", { error });
      });
    }
  }, [channel, messages.length]);

  return {
    messages,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    hasMore,
    loadMore,
    isLoadingMore,
    refetch: () => {
      void query.refetch();
    },
  };
}
